// How you sign in, on Your account: a security check that says what to fix, then
// every passkey and every phone or computer you're signed in on, named in words.
// Removing a passkey or signing a device out asks first, and is audited.

import { deviceName } from "@crm/schemas/devices";
import { CheckCircle } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, api } from "../lib/api.ts";
import { dateTime, longDate, relativeTime } from "../lib/format.ts";
import { Alert, Button, Pill, Section } from "./ui.tsx";

export interface Devices {
	passkeys: { id: string; name: string; addedAt: string | null }[];
	sessions: { id: string; name: string; signedInAt: string; lastUsedAt: string; current: boolean }[];
}

export const devicesQuery = { queryKey: ["devices"] as const, queryFn: () => api<Devices>("/me/devices") };

/** Adds a passkey on this phone or computer, named so the list reads "Safari on iPhone". */
export async function addPasskeyHere(): Promise<boolean> {
	// The sign-in library loads only when it is needed, keeping the first download small.
	const { authClient } = await import("../lib/auth-client.ts");
	const result = await authClient.passkey.addPasskey({ name: deviceName(navigator.userAgent) });
	return !result?.error;
}

type Confirming = { kind: "passkey" | "session"; id: string; name: string } | null;

export function SignInDevices() {
	const queryClient = useQueryClient();
	const q = useQuery(devicesQuery);
	const [message, setMessage] = useState<{ tone: "clear" | "stop"; text: string } | null>(null);
	const [confirming, setConfirming] = useState<Confirming>(null);
	const [askAll, setAskAll] = useState(false);
	const refresh = () => queryClient.invalidateQueries({ queryKey: ["devices"] });
	const failed = (e: unknown) =>
		setMessage({ tone: "stop", text: e instanceof ApiError ? e.message : "That didn't work. Try again." });

	async function addPasskey() {
		setMessage(
			(await addPasskeyHere())
				? { tone: "clear", text: "Passkey added. Next time, tap the email box and choose your passkey." }
				: { tone: "stop", text: "The passkey was not added. Try again, or keep signing in with an email link." },
		);
		void refresh();
	}
	const remove = useMutation({
		mutationFn: (c: NonNullable<Confirming>) =>
			api(c.kind === "passkey" ? `/me/passkeys/${c.id}` : `/me/sessions/${c.id}`, { method: "DELETE" }).then(() => c),
		onSuccess: (c) => {
			setConfirming(null);
			setMessage({
				tone: "clear",
				text:
					c.kind === "passkey"
						? `Removed the passkey on ${c.name}. You'll sign in there by email.`
						: `Signed out ${c.name}.`,
			});
			void refresh();
		},
		onError: failed,
	});
	const others = useMutation({
		mutationFn: () => api<{ signedOut: number }>("/me/sessions/sign-out-others", { body: {} }),
		onSuccess: () => {
			setMessage({ tone: "clear", text: "Signed out everywhere else. Only this device is signed in now." });
			void refresh();
		},
		onError: failed,
	});

	// The question opens below the row; bring its buttons into view, clear of the phone's bottom bar.
	useEffect(() => {
		if (confirming) document.getElementById(`confirm-${confirming.id}`)?.scrollIntoView({ block: "nearest" });
	}, [confirming]);

	const d = q.data;
	const passkeys = d?.passkeys ?? [];
	const sessions = d?.sessions ?? [];
	const fixes = d ? (passkeys.length === 0 ? 1 : 0) + (sessions.length > 1 ? 1 : 0) : 0;

	/** "Remove" or "Sign out", which asks first in place. */
	const ask = ({ kind, id, name }: NonNullable<Confirming>) => {
		if (confirming?.id !== id)
			return (
				<Button variant="danger" className="-mr-2" onClick={() => setConfirming({ kind, id, name })}>
					{kind === "passkey" ? "Remove" : "Sign out"}
					<span className="sr-only"> {name}</span>
				</Button>
			);
		return null;
	};
	const confirm = ({ kind, id, name }: NonNullable<Confirming>) => (
		<div
			id={`confirm-${id}`}
			className="mt-3 scroll-mb-28 rounded-xl border-l-4 border-purple bg-sheet p-3 lg:scroll-mb-4"
		>
			<p className="font-semibold">
				{kind === "passkey"
					? `Remove the passkey on ${name}? You'll sign in there with an email link instead.`
					: `Sign out ${name}? Anyone using it will need to sign in again.`}
			</p>
			<div className="mt-3 flex flex-wrap gap-3">
				<Button variant="secondary" disabled={remove.isPending} onClick={() => confirming && remove.mutate(confirming)}>
					{kind === "passkey" ? "Remove the passkey" : "Sign it out"}
				</Button>
				<Button variant="quiet" onClick={() => setConfirming(null)}>
					{kind === "passkey" ? "Keep it" : "Stay signed in"}
				</Button>
			</div>
		</div>
	);

	return (
		<>
			<Section title="Security check">
				<div aria-live="polite" className="mt-3">
					{message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
				</div>
				{!d ? (
					<p className="text-ink-soft">Checking</p>
				) : fixes === 0 ? (
					<p className="flex items-start gap-2 font-semibold">
						<CheckCircle size={24} weight="fill" aria-hidden="true" className="shrink-0 text-clear" />
						All good. You sign in with a passkey, and only this device is signed in.
					</p>
				) : (
					<ul className="space-y-4">
						{passkeys.length === 0 ? (
							<li className="rounded-xl border-l-4 border-purple bg-sheet p-3">
								<p className="font-semibold">Add a passkey</p>
								<p className="mt-1 text-sm">
									Sign in with Face ID, Touch ID or your fingerprint, without waiting for an email. It's also the safest
									way to sign in.
								</p>
								<Button onClick={addPasskey} className="mt-3 w-full">
									Add a passkey on this device
								</Button>
							</li>
						) : null}
						{sessions.length > 1 ? (
							<li className="rounded-xl border-l-4 border-purple bg-sheet p-3">
								<p className="font-semibold">You're signed in on {sessions.length} devices</p>
								<p className="mt-1 text-sm">
									If you don't recognise one, or you've stopped using it, sign it out below. Or sign out all the others
									at once.
								</p>
								{/* Asks first, like signing out one device: it can't be undone from here. */}
								{askAll ? (
									<div className="mt-3 rounded-xl border-l-4 border-purple bg-card p-3">
										<p className="font-semibold">
											Sign out your other {sessions.length - 1} {sessions.length === 2 ? "device" : "devices"}? You'll
											stay signed in on this one.
										</p>
										<div className="mt-3 flex flex-wrap gap-3">
											<Button
												variant="secondary"
												disabled={others.isPending}
												onClick={() => others.mutate(undefined, { onSettled: () => setAskAll(false) })}
											>
												Sign them out
											</Button>
											<Button variant="quiet" onClick={() => setAskAll(false)}>
												Stay signed in
											</Button>
										</div>
									</div>
								) : (
									<Button variant="secondary" onClick={() => setAskAll(true)} className="mt-3 w-full">
										Sign out everywhere else
									</Button>
								)}
							</li>
						) : null}
					</ul>
				)}
			</Section>

			<Section title="Your sign-in devices">
				{passkeys.length > 0 ? (
					<>
						<h3 className="mt-4 font-semibold">Passkeys</h3>
						<p className="text-sm text-ink-soft">These sign you in with your fingerprint, face or PIN.</p>
						<ul className="mt-2 divide-y divide-rule">
							{passkeys.map((p) => (
								<li key={p.id} className="py-3">
									<div className="grid grid-cols-[1fr_auto] items-center gap-x-3">
										<div className="min-w-0">
											<p className="font-semibold break-words">{p.name}</p>
											<p className="text-sm text-ink-soft">{p.addedAt ? `Added ${longDate(p.addedAt)}` : "Added"}</p>
										</div>
										{ask({ kind: "passkey", id: p.id, name: p.name })}
									</div>
									{confirming?.id === p.id ? confirm({ kind: "passkey", id: p.id, name: p.name }) : null}
								</li>
							))}
						</ul>
						<Button variant="quiet" onClick={addPasskey} className="-ml-2">
							Add a passkey on this device
						</Button>
					</>
				) : null}
				<h3 className={`${passkeys.length > 0 ? "mt-6" : "mt-4"} font-semibold`}>Signed in</h3>
				<p className="text-sm text-ink-soft">Phones and computers where you're signed in now.</p>
				<ul className="mt-2 divide-y divide-rule">
					{sessions.map((s) => (
						<li key={s.id} className="py-3">
							<div className="grid grid-cols-[1fr_auto] items-center gap-x-3">
								<div className="min-w-0">
									<p className="flex flex-wrap items-center gap-2 font-semibold break-words">
										{s.name}
										{s.current ? <Pill tone="good">This device</Pill> : null}
									</p>
									<p className="text-sm text-ink-soft">
										{s.current
											? `Signed in ${dateTime(s.signedInAt)}`
											: `Last used ${relativeTime(s.lastUsedAt).toLowerCase()}`}
									</p>
								</div>
								{s.current ? <span /> : ask({ kind: "session", id: s.id, name: s.name })}
							</div>
							{confirming?.id === s.id ? confirm({ kind: "session", id: s.id, name: s.name }) : null}
						</li>
					))}
				</ul>
			</Section>
		</>
	);
}
